import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaCopy,
  FaDollarSign,
  FaEnvelope,
  FaExternalLinkAlt,
  FaEye,
  FaFacebook,
  FaHandshake,
  FaPercentage,
  FaQrcode,
  FaShareAlt,
} from "react-icons/fa";
import QRCode from "react-qr-code";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { apiUrl } from "../config";
import { UserAuth } from "../contexts/AuthContext";
import "react-quill/dist/quill.snow.css";

// Reuse Quill's editor CSS to render saved instructions, so formatting in the
// admin editor matches what team members see.
const isInstructionsEmpty = (html) =>
  !html || html === "<p><br></p>" || html.replace(/<[^>]+>/g, "").trim() === "";

const formatChartDay = (yyyyMmDd) => {
  const [, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}`;
};

const pickName = (raw, fallback = "") => {
  if (!raw) return fallback;
  if (typeof raw === "string") return raw;
  return raw.en || raw.fr || fallback;
};

const TeamMemberDashboard = ({ campaignId, agentId, embedded = false }) => {
  const { user } = UserAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [socialShareTitle, setSocialShareTitle] = useState("");
  const [socialShareBody, setSocialShareBody] = useState("");
  const [teamInstructions, setTeamInstructions] = useState("");
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  useEffect(() => {
    setCanNativeShare(
      typeof navigator !== "undefined" && typeof navigator.share === "function",
    );
  }, []);

  const resolvedAgentId = agentId || user?.uid;

  const shareUrl = useMemo(() => {
    if (!campaignId || !resolvedAgentId) return "";
    return `${window.location.origin}/support/${campaignId}/${resolvedAgentId}`;
  }, [campaignId, resolvedAgentId]);

  useEffect(() => {
    const load = async () => {
      if (!campaignId || !resolvedAgentId) return;
      setLoading(true);
      try {
        const campaignDoc = await getDoc(doc(db, "campaigns", campaignId));
        if (campaignDoc.exists()) {
          const data = campaignDoc.data();
          setCampaignName(pickName(data.name));
          setSocialShareTitle(data.socialShareTitle || "");
          setSocialShareBody(data.socialShareBody || "");
          setTeamInstructions(data.teamInstructions || "");
        }
        const idToken = sessionStorage.getItem("idToken");
        const res = await axios.get(
          `${apiUrl}/campaign/${campaignId}/agent/${resolvedAgentId}/dashboard`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setStats(res.data);
      } catch (err) {
        console.error("Failed to load team member dashboard", err);
        toast({
          title: "Error",
          description: "Could not load your dashboard.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [campaignId, resolvedAgentId, toast]);

  const copyToClipboard = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      toast({
        title: `${label} copied`,
        description: `${label} is on your clipboard.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    });
  };

  const copyShareUrl = () => copyToClipboard(shareUrl, "Link");

  const shareTitle =
    socialShareTitle || `Support ${campaignName || "our campaign"}`;
  const shareText =
    socialShareBody ||
    `Help support ${
      campaignName || "our campaign"
    } by grabbing a digital coupon book.`;
  const emailBody = shareUrl ? `${shareText}\n\n${shareUrl}` : shareText;

  const emailHref = `mailto:?subject=${encodeURIComponent(
    shareTitle,
  )}&body=${encodeURIComponent(emailBody)}`;
  const facebookHref = shareUrl
    ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl,
      )}`
    : "#";

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Native share failed", err);
      toast({
        title: "Share failed",
        description: "Could not open the share menu.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Flex minH="60vh" justify="center" align="center" width="100%">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const totalAmountSold = stats?.totalAmountSold ?? 0;
  const teamTotalAmountSold = stats?.teamTotalAmountSold ?? 0;
  const supporterCount = stats?.supporterCount ?? 0;
  const viewCount = stats?.viewCount ?? 0;
  const conversionRate = stats?.conversionRate;
  const conversionLabel =
    typeof conversionRate === "number"
      ? `${Math.round(conversionRate * 100)}%`
      : "—";
  const last30Days = stats?.last30Days || [];

  return (
    <Box
      width="100%"
      px={embedded ? 0 : { base: 4, sm: 6, md: 10 }}
      py={embedded ? 0 : { base: 6, md: 8 }}
    >
      <VStack
        align="stretch"
        maxWidth={embedded ? undefined : "1200px"}
        margin={embedded ? undefined : "0 auto"}
        spacing={{ base: 6, md: 8 }}
        width="100%"
      >
        {!embedded && (
          <Box textAlign={{ base: "center", md: "left" }}>
            <Heading as="h1" size="lg" color="heading">
              My Dashboard
            </Heading>
            {campaignName && (
              <Text color="text" fontSize="xl">
                {campaignName}
              </Text>
            )}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
          <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
            <Flex justify="space-between" align="flex-start">
              <StatLabel fontSize="lg">Link Views</StatLabel>
              <Icon as={FaEye} boxSize={7} color="green.500" />
            </Flex>
            <StatNumber fontSize="3xl" mt={4}>
              {viewCount}
            </StatNumber>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
            <Flex justify="space-between" align="flex-start">
              <StatLabel fontSize="lg">Supporters</StatLabel>
              <Icon as={FaHandshake} boxSize={7} color="green.500" />
            </Flex>
            <StatNumber fontSize="3xl" mt={4}>
              {supporterCount}
            </StatNumber>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
            <Flex justify="space-between" align="flex-start">
              <Tooltip
                label="Supporters divided by Link Views — the share of visitors who actually bought a coupon book."
                hasArrow
                placement="top"
              >
                <StatLabel fontSize="lg">Conversion Rate</StatLabel>
              </Tooltip>
              <Icon as={FaPercentage} boxSize={7} color="green.500" />
            </Flex>
            <StatNumber fontSize="3xl" mt={4}>
              {conversionLabel}
            </StatNumber>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
            <Flex justify="space-between" align="flex-start">
              <StatLabel fontSize="lg">Amount Sold</StatLabel>
              <Icon as={FaDollarSign} boxSize={7} color="green.500" />
            </Flex>
            <StatNumber fontSize="3xl" mt={4}>
              ${Math.round(totalAmountSold).toLocaleString()}
              <Text as="span" fontSize="xl" color="gray.500">
                /{Math.round(teamTotalAmountSold).toLocaleString()}
              </Text>
            </StatNumber>
          </Stat>
        </SimpleGrid>

        {!isInstructionsEmpty(teamInstructions) && (
          <Box
            bg="infoBackground"
            p={{ base: 4, md: 6 }}
            borderRadius="lg"
            boxShadow="md"
          >
            <Heading size="md" mb={3}>
              From your campaign admin
            </Heading>
            <Box
              className="ql-snow"
              sx={{
                ".ql-editor": {
                  padding: 0,
                  minHeight: "auto",
                  fontFamily: "inherit",
                  fontSize: "md",
                  color: "text",
                },
              }}
            >
              <Box
                className="ql-editor"
                dangerouslySetInnerHTML={{ __html: teamInstructions }}
              />
            </Box>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
            <HStack mb={4}>
              <Heading size="md">My views &amp; sales — last 30 days</Heading>
            </HStack>
            <Box width="100%" height={{ base: "240px", md: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={last30Days}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatChartDay}
                    interval="preserveStartEnd"
                  />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip labelFormatter={(label) => label} />
                  <Legend />
                  <Bar
                    dataKey="views"
                    name="Views"
                    fill="#9AE6B4"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="count"
                    name="Sales"
                    fill="#38A169"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
            <Heading size="md" mb={2}>
              Share your link
            </Heading>
            <Text color="gray.600" mb={4}>
              Every supporter who uses your link is credited to you.
            </Text>
              <VStack align="stretch" spacing={4}>
              {/* Link actions: QR / Open / Copy */}
              <HStack spacing={2}>
                <Tooltip label="Show QR code" hasArrow placement="top">
                  <IconButton
                    aria-label="Show QR code"
                    icon={<FaQrcode />}
                    size="sm"
                    variant="outline"
                    onClick={() => setIsQRModalOpen(true)}
                    isDisabled={!shareUrl}
                  />
                </Tooltip>
                <Tooltip label="Open link in a new tab" hasArrow placement="top">
                  <IconButton
                    as="a"
                    aria-label="Open link in a new tab"
                    icon={<FaExternalLinkAlt />}
                    size="sm"
                    variant="outline"
                    href={shareUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    isDisabled={!shareUrl}
                  />
                </Tooltip>
                <Tooltip label="Copy link" hasArrow placement="top">
                  <IconButton
                    aria-label="Copy share link"
                    icon={<FaCopy />}
                    size="sm"
                    variant="outline"
                    onClick={copyShareUrl}
                    isDisabled={!shareUrl}
                  />
                </Tooltip>
              </HStack>

              {/* Title + message preview with one-click copy */}
              <HStack align="flex-start" spacing={2}>
                <Box
                  flex={1}
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={2}
                  userSelect="text"
                >
                  <Text fontWeight="semibold" mb={1}>
                    {shareTitle}
                  </Text>
                  <Text color="gray.700" whiteSpace="pre-wrap">
                    {shareText}
                  </Text>
                </Box>
                <Tooltip label="Copy title and message" hasArrow>
                  <IconButton
                    aria-label="Copy title and message"
                    icon={<FaCopy />}
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      copyToClipboard(
                        `${shareTitle}\n\n${shareText}`,
                        "Title and message",
                      )
                    }
                  />
                </Tooltip>
              </HStack>

              {/* Social share */}
              <Flex gap={3} flexWrap="wrap">
                {canNativeShare ? (
                  <Button
                    onClick={handleNativeShare}
                    leftIcon={<FaShareAlt />}
                    variant="secondary"
                  >
                    Share
                  </Button>
                ) : (
                  <>
                    <Button
                      as="a"
                      href={facebookHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      leftIcon={<FaFacebook />}
                      variant="outline"
                      colorScheme="facebook"
                    >
                      Facebook
                    </Button>
                    <Button
                      as="a"
                      href={emailHref}
                      leftIcon={<FaEnvelope />}
                      variant="outline"
                    >
                      Email
                    </Button>
                  </>
                )}
              </Flex>
            </VStack>
          </Box>
        </SimpleGrid>

        {/* QR code modal */}
        <Modal
          isOpen={isQRModalOpen}
          onClose={() => setIsQRModalOpen(false)}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Your share link</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} py={2}>
                {shareUrl && (
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <QRCode value={shareUrl} size={256} level="M" />
                  </Box>
                )}
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Scan this code to open your share link.
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setIsQRModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default TeamMemberDashboard;
